export interface MenuBarOptions {
    container?: HTMLElement;
    direction?: "horizontal" | "vertical";
}

export class MenuBar {
    private _element: HTMLElement;
    private _items: MenuBarItem[] = [];
    private _container: HTMLElement = null;

    constructor(options: MenuBarOptions) {
        options = e5.object.merge({
            direction: "horizontal"
        }, options);

        this._element = document.createElement("div");
        this._element.className = "menu-bar menu-bar--" + options.direction;

        this.container = options.container;
    }

    public get direction(): "horizontal" | "vertical" {
        return this._element.classList.contains("menu-bar--horizontal") ? "horizontal" : "vertical";
    }

    public set direction(value: "horizontal" | "vertical") {
        this._element.classList.remove("menu-bar--horizontal", "menu-bar--vertical");
        this._element.classList.add("menu-bar--" + value);
        for (let item of this._items) {
            item.direction = value;
        }
    }

    public add(options: MenuBarItemOptions): MenuBarItem {
        const item = new MenuBarItem(
            this._element,
            options);
        item.direction = this.direction;
        this._items.push(item);
        return item;
    }

    public get container(): HTMLElement {
        return this._container;
    }

    public set container(value: HTMLElement) {
        this._container = value;

        if (value) {
            value.appendChild(this._element);
        } else {
            this._element.remove();
        }
    }
}

export interface MenuBarItemStateValue<Value = any> {
    default: Value;
    active?: Value;
    disabled?: Value;
}

export type StateValue<Value = any> = Value | MenuBarItemStateValue<Value>;

export interface MenuBarItemOptions {
    icon?: StateValue<string>;
    tooltip?: StateValue<string>;
    label?: StateValue<string>;
    active?: boolean;
    disabled?: boolean;
    visible?: boolean;
    onClick?: (button: MenuBarItem) => void;
    onTrigger?: (button: MenuBarItem) => void;
    onToggle?: (button: MenuBarItem) => void;
    onInit?: (button: MenuBarItem) => void;
}

export class MenuBarItem {
    public readonly onClick = e5.Signal.create<[MenuBarItem]>();
    public readonly onTrigger = e5.Signal.create<[MenuBarItem]>();
    public readonly onToggle = e5.Signal.create<[MenuBarItem]>();

    public readonly element: HTMLButtonElement;
    
    private _icon: MenuBarItemStateValue<string> = null;
    private _tooltip: MenuBarItemStateValue<string> = null;
    private _label: MenuBarItemStateValue<string> = null;

    constructor(container: HTMLElement, options: MenuBarItemOptions) {
        options = e5.object.merge({
            active: false,
            disabled: false,
            visible: true
        }, options);

        this.element = document.createElement("button");
        this.element.className = "menu-bar__item";
        this.element.innerHTML = `
        <div class="menu-bar__item-icon"></div>
        <div class="menu-bar__item-label"></div>
        `;

        this.active = options.active;
        this.disabled = options.disabled;

        this.icon = options.icon;
        this.tooltip = options.tooltip;
        this.label = options.label;
        this.visible = options.visible;

        this.element.addEventListener("click", () => {
            this.onClick.dispatch(this);
            if (!this.disabled) {
                this.onTrigger.dispatch(this);
            }
        });

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
        }

        if (typeof options.onTrigger === "function") {
            this.onTrigger.add(options.onTrigger);
        }

        if (typeof options.onToggle === "function") {
            this.onClick.add(options.onToggle);
        }

        if (typeof options.onInit === "function") {
            options.onInit(this);
        }

        container.appendChild(this.element);
    }

    public set icon(value: StateValue<string>) {
        this._icon = typeof value === "string" ? { default: value } : value;
        this.updateIcon();
    }

    public set tooltip(value: StateValue<string>) {
        this._tooltip = typeof value === "string" ? { default: value } : value;
        this.updateTooltip();
    }

    public set label(value: StateValue<string>) {
        this._label = typeof value === "string" ? { default: value } : value;
        this.updateLabel();
    }

    public get active(): boolean {
        return this.element.classList.contains("menu-bar__item--active");
    }

    public set active(value: boolean) {
        if (this.active === value) {
            return;
        }
        this.element.classList.toggle("menu-bar__item--active", value);
        this.updateState();
        this.onToggle.dispatch(this);
    }

    public get visible(): boolean {
        return !this.element.classList.contains("menu-bar__item--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this.element.classList.toggle("menu-bar__item--hidden", !value);
    }

    public get disabled(): boolean {
        return this.element.classList.contains("menu-bar__item--disabled");
    }

    public set disabled(value: boolean) {
        if (this.disabled === value) {
            return;
        }
        this.element.classList.toggle("menu-bar__item--disabled", value);
        this.updateState();
    }

    private updateState(): void {
        this.updateIcon();
        this.updateTooltip();
        this.updateLabel();
    }

    private updateLabel(): void {
        let label = this.getValue(this._label);
        let eLabel = this.element.querySelector(".menu-bar__item-label");
        if (!label) {
            eLabel.textContent = "";
            eLabel.classList.add("e5-hidden");
        } else {
            eLabel.textContent = label || "";
            eLabel.classList.remove("e5-hidden");
        }
    }

    private updateTooltip(): void {
        let tooltip = this.getValue(this._tooltip);
        if (!tooltip) {
            this.element.removeAttribute("data-tooltip");
        } else {
            this.element.setAttribute("data-tooltip", tooltip);
        }
    }

    private updateIcon(): void {
        let icon = this.getValue(this._icon);
        let cls = "menu-bar__item-icon";
        let e = this.element.querySelector("." + cls);
        e.className = cls + " " + (icon || "");
    }

    private getValue<Value>(stateValue: MenuBarItemStateValue<Value>): Value | null {
        if (!stateValue) {
            return null;
        }

        if (this.disabled
            && stateValue.disabled !== undefined) {
            return stateValue.disabled;
        }
        else if (this.active
            && stateValue.active !== undefined) {
            return stateValue.active;
        }

        return stateValue.default;
    }
    public set direction(value: "horizontal" | "vertical") {
        this.element.setAttribute("data-tooltiplayout", value === "horizontal" ? "top bottom" : "left right");
    }
}